import { STRUCTURES } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { cosA, dist2, idiv, imax, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { deriveSeed, rngChance, rngInt, rngRange, seedRng } from '../math/rng.ts';
import type { BattleState, Hero, Structure, Zone } from '../types.ts';
import { damageEnemy, damageHero, damageStructure, enemiesNear, ev, healHero } from './combat.ts';
import { turnedOnHeroes } from './vigil.ts';

/*
 * The day's omens that play on the field (SPEC §13.4): the storm's lightning, marked on the ground a moment
 * before it falls and hitting heroes and nightmares alike, and the falling stars, which land a few steps
 * from a hero and empower whoever gets there first.
 */

/** A struck bolt lingers this long for the eye, harmless. */
const AFTERGLOW = 6;

const standing = (s: BattleState): Hero[] => s.heroes.filter((h) => h.downTicks === 0);
const fighting = (s: BattleState): boolean => s.enemies.some((e) => e.hp > 0 && e.kind !== 'boneWall');

/**
 * The towers a bastion storm strikes and the lightning rods that take it (docs/PRESAGIOS.md §3.2): shooting towers
 * standing on their own (not the beacon, the belfry, one mounted on a wall, nor walls, gates, traps or the keep).
 * Like the beacons in combat.ts, the list grows by push and is replaced when something is removed, so its identity
 * and length tell whether it is current (one that falls is skipped by `destroyed`).
 */
const SKY = new WeakMap<Structure[], { n: number; towers: Structure[]; rods: { st: Structure; x: number; y: number }[] }>();
const skyTargets = (s: BattleState): { towers: Structure[]; rods: { st: Structure; x: number; y: number }[] } => {
  const m = SKY.get(s.structures);
  if (m && m.n === s.structures.length) return m;
  const towers = s.structures.filter((st) => !st.mount && STRUCTURES[st.type].category === 'tower' && st.type !== 'beacon' && st.type !== 'belfry');
  const rods = s.structures.filter((st) => st.type === 'rod').map((st) => ({ st, ...hexToWorld(st.q, st.r) }));
  const v = { n: s.structures.length, towers, rods };
  SKY.set(s.structures, v);
  return v;
};

/** The storm's own draws, from the battle's seed and the tick: they move none of the wave's other draws. */
const SKY_SALT = 0x5ca7b01;

/** A bastion storm: now and then a bolt marked on a tower, or on the lightning rod nearest it that reaches it. */
const skyTick = (s: BattleState): void => {
  const b = s.rules?.skyBolts;
  if (!b || s.tick === 0 || s.tick % b.every !== 0 || s.result !== 'running' || !fighting(s)) return;
  const { towers, rods } = skyTargets(s);
  // Not on one Bruni took and turned on the heroes: felling it would help them (a sealed one he took still counts).
  const standingTowers = towers.filter((st) => !st.destroyed && !turnedOnHeroes(s, st));
  if (!standingTowers.length) return;
  const st = standingTowers[rngInt(seedRng(deriveSeed(s.seed ^ SKY_SALT, s.tick)), standingTowers.length)] as Structure;
  const c = hexToWorld(st.q, st.r);
  let rod: { x: number; y: number } | null = null;
  let best = STRUCTURES.rod.range * STRUCTURES.rod.range;
  for (const o of rods) {
    if (o.st.destroyed) continue;
    const d = dist2(c.x, c.y, o.x, o.y);
    if (d <= best && (!rod || d < best)) {
      rod = o;
      best = d;
    }
  }
  const tally = (s.stats.sky ??= { bolts: 0, rods: 0 });
  tally.bolts = (tally.bolts ?? 0) + 1;
  if (rod) tally.rods = (tally.rods ?? 0) + 1;
  // A bolt the rod takes falls on it all the same, for the eye, and does nothing.
  const at = rod ?? c;
  const damage = rod ? 0 : imax(1, mulBp(st.maxHp, b.structureBp));
  s.zones.push({ id: s.nextId++, kind: 'bolt', x: at.x, y: at.y, radius: b.radius, cells: [], ticks: b.warn + AFTERGLOW, every: 1, damage, slowBp: 0, pid: '', warn: b.warn });
};

/**
 * The tower a bastion storm's bolt marked, if it still stands there. One Bruni took during the warning is spared (the
 * bolt still counts as marked).
 */
const skyStrike = (s: BattleState, z: Zone): void => {
  if (z.damage <= 0) return;
  for (const st of skyTargets(s).towers) {
    if (st.destroyed) continue;
    const c = hexToWorld(st.q, st.r);
    if (c.x === z.x && c.y === z.y) {
      if (!turnedOnHeroes(s, st)) damageStructure(s, st, z.damage, null, 'lightning');
      return;
    }
  }
};

/** Each tick: a bolt now and then, a star now and then, while there is a fight. */
export const omenTick = (s: BattleState): void => {
  skyTick(s);
  const r = s.rules;
  if ((!r?.bolts && !r?.stars) || s.result !== 'running' || !fighting(s)) return;
  const heroes = standing(s);
  if (!heroes.length) return;
  const b = r.bolts;
  if (b && s.tick % b.every === 0) {
    // Mostly near a hero (step aside); sometimes on a nightmare close to the heroes (lead the rest under it).
    const near = enemiesNear(s, (heroes[0] as Hero).x, (heroes[0] as Hero).y, 7000, (e) => e.kind !== 'boneWall');
    let x: number, y: number;
    if (near.length && rngChance(s.rng, b.onEnemyBp)) {
      const e = near[rngInt(s.rng, near.length)] as (typeof near)[number];
      x = e.x;
      y = e.y;
    } else {
      const h = heroes[rngInt(s.rng, heroes.length)] as Hero;
      x = h.x + rngRange(s.rng, -500, 500);
      y = h.y + rngRange(s.rng, -500, 500);
    }
    s.zones.push({ id: s.nextId++, kind: 'bolt', x, y, radius: b.radius, cells: [], ticks: b.warn + AFTERGLOW, every: 1, damage: b.heroDamage, slowBp: 0, pid: '', warn: b.warn });
  }
  const st = r.stars;
  if (st && s.tick % st.every === idiv(st.every, 2) && !s.zones.some((z) => z.kind === 'star')) {
    const h = heroes[rngInt(s.rng, heroes.length)] as Hero;
    const spot = starSpot(s, h, st.minDist, st.maxDist);
    s.zones.push({ id: s.nextId++, kind: 'star', x: spot.x, y: spot.y, radius: st.radius, cells: [], ticks: st.warn + st.ticks, every: 1, damage: st.impactDamage, slowBp: 0, pid: '', warn: st.warn });
  }
};

/** A few steps from the hero, on ground a hero can walk (closer in if there is none that far). */
const starSpot = (s: BattleState, h: Hero, lo: number, hi: number): { x: number; y: number } => {
  for (let k = 0; k < 8; k++) {
    const a = rngInt(s.rng, 1024);
    const d = idiv(rngRange(s.rng, lo, hi) * (8 - k), 8);
    const x = h.x + idiv(cosA(a) * d, 10000);
    const y = h.y + idiv(sinA(a) * d, 10000);
    const c = cellAtWorld(s, x, y);
    if (c >= 0 && heroWalkable(s, c)) return { x, y };
  }
  return { x: h.x, y: h.y };
};

/** The omens' zones: a bolt counts down and strikes; a star counts down, lands, and waits for a hero. */
export const omenZone = (s: BattleState, z: Zone): void => {
  if (z.warn === undefined) return;
  if (z.warn > 0) {
    z.warn--;
    if (z.warn > 0) return;
    if (z.kind === 'bolt' && s.rules?.skyBolts) {
      // A bastion storm: only the tower under it (docs/PRESAGIOS.md §3.2).
      skyStrike(s, z);
      ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'bolt' });
      z.warn = -1;
      z.ticks = AFTERGLOW;
      return;
    }
    if (z.kind === 'bolt') {
      for (const h of s.heroes) if (h.downTicks === 0 && withinRange(z.x, z.y, h.x, h.y, z.radius)) damageHero(s, h, z.damage);
      const hit = s.rules?.bolts?.enemyDamage ?? 0;
      for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => o.kind !== 'boneWall')) damageEnemy(s, e, hit, { pid: '', ignoresArmor: true });
      ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'bolt' });
      z.warn = -1;
      z.ticks = AFTERGLOW;
      return;
    }
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => o.kind !== 'boneWall')) damageEnemy(s, e, z.damage, { pid: '', ignoresArmor: true });
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'starfall' });
    z.warn = -1;
    return;
  }
  if (z.kind !== 'star' || z.warn !== -1) return;
  const st = s.rules?.stars;
  if (!st) return;
  // The first hero (by id) to stand on it takes its light.
  const h = s.heroes.find((o) => o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius));
  if (!h) return;
  h.starUntil = s.tick + st.buffTicks;
  healHero(s, h, mulBp(h.stats.maxHp, st.healBp), h.pid);
  ev(s, { t: 'skill', hero: h.id, skill: 'starPick', x: z.x, y: z.y });
  z.ticks = 0;
};
